/**
 * Browser push, client side — V6-01 §4, repaired by V7-01 §1.
 *
 * The permission prompt is raised from one explicit click and nowhere else.
 * Browsers penalise sites that prompt on load — Chrome and Firefox both
 * quietly deny an unsolicited prompt for a while afterwards — so an
 * auto-prompt does not merely annoy, it can lose the ability to ask at all.
 *
 * ## The V7-01 root cause, recorded where it happened
 *
 * V6-01 asked for the VAPID public key **before** calling
 * `Notification.requestPermission()`:
 *
 * ```
 * const { publicKey } = (await api.get('/push/public-key')).data;   // ← network
 * const permission = await Notification.requestPermission();        // ← too late
 * ```
 *
 * `requestPermission()` requires *transient user activation*. Awaiting a
 * network round trip — worse, one that may transparently refresh an access
 * token first — spends the activation the click granted. Firefox then resolves
 * the promise with `default` **without ever showing a prompt**, which is why
 * the founder's site never appeared in Firefox's notification list: there was
 * nothing to list. The code saw `default`, threw "permission was not granted",
 * and a toast blamed the user for a decision they were never offered.
 *
 * So permission is now asked for **first**, with nothing awaited before it, and
 * `requestPushPermission()` is deliberately separate from the subscribe flow so
 * that a click handler can call it as its own first statement.
 * `pushGesture.test.ts` enforces that ordering structurally, because it is the
 * kind of mistake that reads as harmless in review.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface PushDevice {
  _id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** The VAPID public key travels as base64url; `subscribe()` wants raw bytes. */
function decodeKey(base64Url: string): Uint8Array {
  const padded = `${base64Url}${'='.repeat((4 - (base64Url.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function usePushDevices() {
  return useQuery({
    queryKey: ['push-devices'],
    queryFn: async () => (await api.get<PushDevice[]>('/push/subscriptions')).data,
  });
}

export function usePushPublicKey() {
  return useQuery({
    queryKey: ['push-public-key'],
    queryFn: async () =>
      (await api.get<{ publicKey: string | null; configured: boolean }>('/push/public-key')).data,
  });
}

export class PushSetupError extends Error {}

/**
 * Raise the browser's permission prompt.
 *
 * **Call this as the first statement of a click handler, with nothing awaited
 * before it.** Everything the subscription needs is fetched afterwards; see the
 * module header for what happens when that order is reversed.
 *
 * Returns the resulting permission rather than throwing, because `default` —
 * the user closed the prompt without choosing — is an ordinary outcome and not
 * an error to report as one.
 */
export async function requestPushPermission(): Promise<NotificationPermission> {
  if (!pushSupported()) return 'denied';
  // Already answered: asking again shows nothing and, in Chrome, counts
  // against the site.
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

/**
 * Key → service worker → subscription → server row.
 *
 * Assumes permission is already `granted`; that is `requestPushPermission()`'s
 * job and it has to happen inside the gesture. Each step has its own failure
 * the user can act on, so each throws its own sentence — "Could not enable
 * push" is what makes a support ticket.
 */
export async function subscribeThisDevice(): Promise<PushDevice> {
  if (!pushSupported()) {
    throw new PushSetupError('This browser cannot receive push notifications.');
  }
  if (Notification.permission !== 'granted') {
    throw new PushSetupError(
      Notification.permission === 'denied'
        ? 'Notifications are blocked for this site. Allow them in the browser’s site settings, then try again.'
        : 'Notification permission has not been granted on this device yet.',
    );
  }

  const { publicKey } = (await api.get<{ publicKey: string | null }>('/push/public-key')).data;
  if (!publicKey) {
    throw new PushSetupError(
      'Push is not configured for this installation yet — an administrator has to generate the keys.',
    );
  }

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();

  /**
   * An existing subscription is reused only while it was minted from the key
   * the server holds now.
   *
   * V7-01 §2.2: when the founder regenerates VAPID keys, every stored
   * subscription is signed by a key the server can no longer authenticate
   * with, and the push service rejects it. V6-01 reused it regardless, which
   * is why key rotation meant "everyone must click the button again". Comparing
   * the key and re-subscribing when it differs is what makes the next login
   * self-heal instead.
   */
  const wanted = decodeKey(publicKey);
  const subscription =
    existing && sameApplicationServerKey(existing, wanted)
      ? existing
      : await resubscribe(registration, existing, wanted);

  const json = subscription.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new PushSetupError('The browser returned an unusable subscription.');
  }

  return (
    await api.post<PushDevice>('/push/subscriptions', {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    })
  ).data;
}

/** Drops the stale subscription before minting a new one, so no orphan remains. */
async function resubscribe(
  registration: ServiceWorkerRegistration,
  existing: PushSubscription | null,
  key: Uint8Array,
): Promise<PushSubscription> {
  if (existing) {
    await existing.unsubscribe().catch(() => undefined);
    // Best effort: the server prunes on 404/410 anyway, but telling it now
    // keeps the device list honest between the two.
    await api
      .delete('/push/subscriptions', { endpoint: existing.endpoint })
      .catch(() => undefined);
  }
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key as unknown as BufferSource,
  });
}

/** Whether a live subscription was minted from this application server key. */
export function sameApplicationServerKey(
  subscription: Pick<PushSubscription, 'options'>,
  key: Uint8Array,
): boolean {
  const applied = subscription.options?.applicationServerKey;
  // Browsers that do not expose it (older Safari) get the benefit of the doubt:
  // re-subscribing every login would be worse than missing a rotation.
  if (!applied) return true;
  const bytes = new Uint8Array(applied as ArrayBuffer);
  if (bytes.length !== key.length) return false;
  return bytes.every((byte, index) => byte === key[index]);
}

export function useEnablePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: subscribeThisDevice,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['push-devices'] }),
  });
}

export function useRemovePushDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/push/subscriptions/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['push-devices'] }),
  });
}

/**
 * The zero-click half of V7-01 §2.
 *
 * Runs on every login where the browser has already granted permission. It
 * either finds a healthy subscription matching the current server key and does
 * nothing, or it quietly makes one — which covers all three ways a working
 * device goes quiet without the person doing anything wrong:
 *
 *  - the browser rotated the endpoint (they do, without warning);
 *  - the founder regenerated the VAPID keys in `/platform`;
 *  - the subscription was pruned server-side after a 404/410.
 *
 * Returns what it did, so the caller can log it and a test can assert it.
 * Never prompts, never toasts: permission survives all of the above, so there
 * is nothing to ask and nothing worth interrupting anyone about.
 */
export type ResubscribeOutcome =
  | 'unsupported'
  | 'not-granted'
  | 'not-configured'
  | 'already-current'
  | 'resubscribed'
  | 'failed';

export async function ensureSubscribed(): Promise<ResubscribeOutcome> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission !== 'granted') return 'not-granted';

  try {
    const { publicKey } = (await api.get<{ publicKey: string | null }>('/push/public-key')).data;
    if (!publicKey) return 'not-configured';

    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    const wanted = decodeKey(publicKey);

    if (existing && sameApplicationServerKey(existing, wanted)) {
      // The browser still holds it, but the server may not — a pruned row is
      // invisible from here, so re-registering is cheap insurance and the
      // endpoint is the idempotency key.
      const json = existing.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      if (json.endpoint && json.keys?.p256dh && json.keys.auth) {
        await api.post('/push/subscriptions', {
          endpoint: json.endpoint,
          keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        });
      }
      return 'already-current';
    }

    await subscribeThisDevice();
    return 'resubscribed';
  } catch {
    // Silent by design — this runs on every login and nobody asked for it.
    // A real failure is visible in Settings → Notifications' diagnostics panel,
    // which is the screen built for exactly this question.
    return 'failed';
  }
}

/** Sends a real push to this person's devices — the §1 round-trip check. */
/** V9-03 §0 — where the keys came from, in the words the panel shows. */
export type PushKeySource = 'platform' | 'env' | 'none' | 'unreadable';

export interface PushTestResult {
  devices: number;
  sent: number;
  failed: number;
  transport: 'webpush' | 'console';
  /** §0 — named, never inferred from the transport. */
  keySource: PushKeySource;
  /** §0 — set when configuration overrides the decision, e.g. a console pin. */
  pin: string | null;
  /** Whether a usable pair resolved at all, whatever the transport is. */
  configured: boolean;
  /** §2 — the rows hop four will be reported against. */
  deliveryIds: string[];
  details: { label: string; ok: boolean; error: string | null }[];
}

/** §2 — the four hops, as the panel shows them. */
export interface DeliveryHops {
  recorded: boolean;
  accepted: boolean;
  displayed: boolean;
}

export function usePushDeliveryHops() {
  return useMutation({
    mutationFn: async (deliveryIds: string[]) =>
      (
        await api.post<DeliveryHops>('/push/test/hops', { deliveryIds })
      ).data,
  });
}

export function useSendTestPush() {
  return useMutation({
    mutationFn: async () => (await api.post<PushTestResult>('/push/test', {})).data,
  });
}
